export enum UserRoleName {
  TEACHER = 'TEACHER',
  ADMIN = 'ADMIN'
}

export interface UserRole {
  name: UserRoleName;
  permissions: { name: string }[];
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: UserRole[];
}

export interface AuthTokenClaims {
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userRoles: string;
  exp?: number;
}

export class NotAnAccountError extends Error {}

export class InvalidVerificationCodeError extends Error {}

export class InvalidRefreshTokenError extends Error {}

const authUrl = (path: string) => `${process.env.REACT_APP_ESTUDI_API_URL}/estudi/auth/${path}`;

export const userFromClaims = (claims: AuthTokenClaims): AuthUser => ({
  id: claims.userId,
  email: claims.userEmail,
  firstName: claims.userFirstName,
  lastName: claims.userLastName,
  roles: JSON.parse(claims.userRoles) as UserRole[]
});

export const isAdmin = (user: AuthUser | undefined): boolean =>
  !!user?.roles.some((role) => role.name === UserRoleName.ADMIN);

export const sendCode = async (email: string): Promise<void> => {
  const response = await fetch(authUrl('send-code'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });

  if (!response.ok) {
    if (response.status === 400) {
      throw new NotAnAccountError();
    }

    throw new Error(`Invalid api response with status ${response.status}`);
  }
};

export const verifyCode = async (email: string, code: string): Promise<string> => {
  const response = await fetch(authUrl('verify-code'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
    credentials: 'include' // The api returns the refresh token in a cookie
  });

  if (!response.ok) {
    if (response.status === 400) {
      throw new InvalidVerificationCodeError();
    }

    throw new Error(`Invalid api response with status ${response.status}`);
  }

  const { token } = await response.json();

  return token as string;
};

export const refreshToken = async (): Promise<string> => {
  const response = await fetch(authUrl('refresh-token'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include' // The refresh token travels in the cookie
  });

  if (!response.ok) {
    throw new InvalidRefreshTokenError();
  }

  const { token } = await response.json();

  return token as string;
};

export const logout = async (): Promise<void> => {
  await fetch(authUrl('logout'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include'
  });
};
